import { apiClient } from "./client";
import type { Paginated, PageQuery } from "@/types/common";
import type { Teacher, TeacherCertificate, TeacherCertificateInput, TeacherCreateInput, TeacherGroupRate, TeacherProfileInput } from "@/types/teacher";

export interface TeachersQuery extends PageQuery {
  branchId?: string;
  status?: string;
  search?: string;
}

export async function fetchTeachers(query: TeachersQuery): Promise<Paginated<Teacher>> {
  const { data } = await apiClient.get<Paginated<Teacher>>("/teachers", { params: query });
  return data;
}

export async function fetchTeacher(id: string): Promise<Teacher> {
  const { data } = await apiClient.get<Teacher>(`/teachers/${id}`);
  return data;
}

export async function createTeacher(payload: TeacherCreateInput): Promise<Teacher> {
  const { data } = await apiClient.post<Teacher>("/teachers", payload);
  return data;
}

export async function updateTeacher(id: string, payload: Partial<TeacherCreateInput>): Promise<Teacher> {
  const { data } = await apiClient.patch<Teacher>(`/teachers/${id}`, payload);
  return data;
}

export async function approveTeacher(id: string): Promise<Teacher> {
  const { data } = await apiClient.post<Teacher>(`/teachers/${id}/approve`);
  return data;
}

export async function fetchPendingTeachers(): Promise<Teacher[]> {
  const result = await fetchTeachers({ status: "PENDING", limit: 100 });
  return result.items;
}

export async function rejectTeacher(id: string): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>(`/teachers/${id}/reject`);
  return data;
}

export async function fetchTeacherRates(id: string): Promise<TeacherGroupRate[]> {
  const { data } = await apiClient.get<TeacherGroupRate[]>(`/teachers/${id}/rates`);
  return data;
}

export async function setTeacherGroupRate(id: string, groupId: string, percent: number | null): Promise<{ groupId: string; percent: number | null }> {
  const { data } = await apiClient.put<{ groupId: string; percent: number | null }>(`/teachers/${id}/rates/${groupId}`, { percent: percent ?? undefined });
  return data;
}

export async function updateTeacherProfile(id: string, payload: TeacherProfileInput): Promise<Teacher> {
  const { data } = await apiClient.patch<Teacher>(`/teachers/${id}/profile`, payload);
  return data;
}

export async function fetchTeacherCertificates(id: string): Promise<TeacherCertificate[]> {
  const { data } = await apiClient.get<TeacherCertificate[]>(`/teachers/${id}/certificates`);
  return data;
}

export async function addTeacherCertificate(id: string, payload: TeacherCertificateInput): Promise<TeacherCertificate> {
  const { data } = await apiClient.post<TeacherCertificate>(`/teachers/${id}/certificates`, payload);
  return data;
}

export async function deleteTeacherCertificate(id: string, certificateId: string): Promise<void> {
  await apiClient.delete(`/teachers/${id}/certificates/${certificateId}`);
}
